import React from 'react';
import { Button, Table, Tooltip } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined } from '@ant-design/icons';
import { InstrumentConfigResponse } from '../../../services/instrumentConfigApi';
import { formatNumber } from '../../../utils/numberFormat';
import { isFuturesExchange } from '../../../utils/instrumentKind';
import useIsMobile from '../../../hooks/useIsMobile';
import { TableWrapper } from '../styles';

const GREEN = '#0ECB81';
const RED = '#F6465D';
const TEXT = '#EAECEF';
const MUTED = '#848E9C';

/** Delete only once fully drained: no committed cash, no open position. Mirrors the BE guard. */
const canDeleteAllocation = (config: InstrumentConfigResponse): boolean =>
  config.status !== 'CLOSED' &&
  Number(config.currentCapital) === 0 &&
  Number(config.holdingsCost) === 0 &&
  Number(config.heldQty) === 0;

const deleteAllocationHint = (canDelete: boolean): string =>
  canDelete
    ? 'Delete this fully-drained allocation (history is kept).'
    : 'Withdraw all capital and close positions before deleting this allocation.';

interface Props {
  configs: InstrumentConfigResponse[];
  onDeposit: (config: InstrumentConfigResponse) => void;
  onWithdraw: (config: InstrumentConfigResponse) => void;
  onDelete?: (config: InstrumentConfigResponse) => void;
}

const fmt = (s: string | number | null | undefined): string => {
  const n = Number(s);
  return s === null || s === undefined || !Number.isFinite(n) ? '—' : formatNumber(n);
};

const pnlColor = (s: string): string => (Number(s) < 0 ? RED : GREEN);

// An older BE sends only the net figure: show it rather than a blank, and no Withdrawn column.
const hasGross = (configs: InstrumentConfigResponse[]): boolean =>
  configs.some((c) => c.withdrawnTotal !== undefined);
const depositedOf = (cfg: InstrumentConfigResponse): string => cfg.depositedTotal ?? cfg.netDeposits;

/** Cash moved onto (+) or off (−) this leg from the pair's other legs, when there is any. */
const transferNote = (cfg: InstrumentConfigResponse): string | null => {
  const n = Number(cfg.transferredNet ?? 0);
  if (!Number.isFinite(n) || n === 0) return null;
  return `${n > 0 ? '+' : '−'}${formatNumber(Math.abs(n))} from other legs`;
};

const TIP = {
  deposited: 'Cash you put into this leg. Cash the pair moved between its own legs is shown apart.',
  withdrawn: 'Cash taken out of this leg back to your account, including at the end of the pair.',
  realized: 'Realized PnL the bot has already added to the capital: capital + withdrawn − deposited. Not the same as its lifetime realized PnL.',
  cost: 'Stock: cost basis of the shares the bot holds. Futures: collateral the broker holds as margin against the open contracts.',
  held: 'Shares held on a stock leg, open contracts on the futures leg.',
  cash: 'Stock: the part of the capital not spent on shares. Futures: not shown, the leg\'s cash is its margin.',
  stockValue: 'Stock: held qty × latest matched price. Empty when there is no price yet.',
};

const SymbolCell: React.FC<{ config: InstrumentConfigResponse }> = ({ config }) => {
  const futures = isFuturesExchange(config.exchange);
  return (
    <Tooltip title={`${config.exchange}:${config.symbol}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
        <span style={{ fontWeight: 700, color: TEXT }}>{config.symbol}</span>
        <span style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {futures ? 'Futures' : 'Stock'}
        </span>
      </div>
    </Tooltip>
  );
};

const heldLabel = (config: InstrumentConfigResponse): string => {
  if (!isFuturesExchange(config.exchange)) return fmt(config.heldQty);
  const n = Number(config.heldQty);
  return `${fmt(config.heldQty)} ${Math.abs(n) === 1 ? 'contract' : 'contracts'}`;
};

// The server's marketValue is idle cash + shares at market; split back into its two halves.
const cashOf = (config: InstrumentConfigResponse): number =>
  Number(config.effectiveCapital ?? config.currentCapital) - Number(config.holdingsCost);
const cashLabel = (config: InstrumentConfigResponse): string =>
  isFuturesExchange(config.exchange) ? '—' : fmt(cashOf(config));
const stockValueLabel = (config: InstrumentConfigResponse): string =>
  isFuturesExchange(config.exchange) || config.marketPrice == null || config.marketValue == null
    ? '—'
    : fmt(Number(config.marketValue) - cashOf(config));

const Actions: React.FC<{
  config: InstrumentConfigResponse;
  onDeposit: () => void;
  onWithdraw: () => void;
  onDelete?: () => void;
  stretch?: boolean;
}> = ({ config, onDeposit, onWithdraw, onDelete, stretch }) => {
  const disabled = config.status === 'CLOSED';
  const canDelete = canDeleteAllocation(config);
  const grow = stretch ? { flex: 1 } : undefined;
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
      <Button
        size="small"
        icon={<ArrowDownOutlined />}
        onClick={onDeposit}
        disabled={disabled}
        style={{ ...grow, background: disabled ? undefined : GREEN, borderColor: disabled ? undefined : GREEN, color: disabled ? undefined : '#fff' }}
      >
        Deposit
      </Button>
      <Button
        size="small"
        icon={<ArrowUpOutlined />}
        onClick={onWithdraw}
        disabled={disabled}
        style={{ ...grow, background: disabled ? undefined : RED, borderColor: disabled ? undefined : RED, color: disabled ? undefined : '#fff' }}
      >
        Withdraw
      </Button>
      {onDelete && (
        <Tooltip title={deleteAllocationHint(canDelete)}>
          <span>
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={onDelete}
              disabled={!canDelete}
              aria-label="Delete allocation"
            />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

const MobileStat: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: 11, color: MUTED }}>{label}</div>
    <div
      title={value}
      style={{ fontSize: 13, fontWeight: 600, color: color ?? TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
    >
      {value}
    </div>
  </div>
);

/** A phone gets one block per leg: the symbol, its figures in a grid, full-width actions. */
const MobileList: React.FC<Props> = ({ configs, onDeposit, onWithdraw, onDelete }) => (
  <div>
    {configs.map((cfg, i) => {
      const futures = isFuturesExchange(cfg.exchange);
      return (
        <div key={cfg.id} style={{ padding: '12px 0', borderTop: i === 0 ? 'none' : '1px solid #2B3139' }}>
          <SymbolCell config={cfg} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginTop: 10 }}>
            <MobileStat label="Deposited" value={fmt(depositedOf(cfg))} />
            {cfg.withdrawnTotal !== undefined && <MobileStat label="Withdrawn" value={fmt(cfg.withdrawnTotal)} />}
            <MobileStat label="Realized PnL" value={fmt(cfg.realizedCredited)} color={pnlColor(cfg.realizedCredited)} />
            <MobileStat label={futures ? 'Margin posted' : 'Position cost'} value={fmt(cfg.holdingsCost)} />
            <MobileStat label={futures ? 'Open contracts' : 'Held'} value={fmt(cfg.heldQty)} />
            {!futures && <MobileStat label="Cash" value={cashLabel(cfg)} />}
            {!futures && <MobileStat label="Stock value" value={stockValueLabel(cfg)} />}
          </div>
          <div style={{ marginTop: 10 }}>
            <Actions
              config={cfg}
              stretch
              onDeposit={() => onDeposit(cfg)}
              onWithdraw={() => onWithdraw(cfg)}
              onDelete={onDelete ? () => onDelete(cfg) : undefined}
            />
          </div>
        </div>
      );
    })}
  </div>
);

const header = (title: string, tip: string) => (
  <Tooltip title={tip}><span style={{ cursor: 'help' }}>{title}</span></Tooltip>
);

/** Per-symbol allocations (grid and pair legs) as one table; a list on a phone. */
const InstrumentConfigTable: React.FC<Props> = (props) => {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileList {...props} />;
  const { configs, onDeposit, onWithdraw, onDelete } = props;
  return (
    <TableWrapper>
      <Table<InstrumentConfigResponse>
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={configs}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Symbol',
            key: 'symbol',
            fixed: 'left' as const,
            render: (_: unknown, cfg) => <SymbolCell config={cfg} />,
          },
          {
            title: header('Deposited', TIP.deposited),
            key: 'deposited',
            align: 'right' as const,
            render: (_: unknown, cfg) => {
              const note = transferNote(cfg);
              return (
                <span style={{ color: MUTED }}>
                  {fmt(depositedOf(cfg))}
                  {note && <div style={{ fontSize: 10 }}>{note}</div>}
                </span>
              );
            },
          },
          ...(hasGross(configs)
            ? [{
                title: header('Withdrawn', TIP.withdrawn),
                key: 'withdrawn',
                align: 'right' as const,
                render: (_: unknown, cfg: InstrumentConfigResponse) => (
                  <span style={{ color: MUTED }}>{fmt(cfg.withdrawnTotal)}</span>
                ),
              }]
            : []),
          {
            title: header('Realized PnL', TIP.realized),
            key: 'realized',
            align: 'right' as const,
            render: (_: unknown, cfg) => <span style={{ color: pnlColor(cfg.realizedCredited) }}>{fmt(cfg.realizedCredited)}</span>,
          },
          {
            title: header('Position cost', TIP.cost),
            key: 'cost',
            align: 'right' as const,
            render: (_: unknown, cfg) => (
              <span style={{ color: TEXT }}>
                {fmt(cfg.holdingsCost)}
                {isFuturesExchange(cfg.exchange) && <span style={{ color: MUTED, fontSize: 10, marginLeft: 4 }}>margin</span>}
              </span>
            ),
          },
          {
            title: header('Held', TIP.held),
            key: 'held',
            align: 'right' as const,
            render: (_: unknown, cfg) => <span style={{ color: TEXT, whiteSpace: 'nowrap' }}>{heldLabel(cfg)}</span>,
          },
          {
            title: header('Cash', TIP.cash),
            key: 'cash',
            align: 'right' as const,
            render: (_: unknown, cfg) => <span style={{ color: TEXT }}>{cashLabel(cfg)}</span>,
          },
          {
            title: header('Stock value', TIP.stockValue),
            key: 'stockValue',
            align: 'right' as const,
            render: (_: unknown, cfg) => <span style={{ color: TEXT }}>{stockValueLabel(cfg)}</span>,
          },
          {
            title: '',
            key: 'actions',
            align: 'right' as const,
            // Pinned so Deposit / Withdraw stay in reach when the figures need a scroll.
            fixed: 'right' as const,
            render: (_: unknown, cfg) => (
              <Actions
                config={cfg}
                onDeposit={() => onDeposit(cfg)}
                onWithdraw={() => onWithdraw(cfg)}
                onDelete={onDelete ? () => onDelete(cfg) : undefined}
              />
            ),
          },
        ]}
      />
    </TableWrapper>
  );
};

export default InstrumentConfigTable;
